import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WalletService } from '../services/wallet.service';
import { ToastService } from '@shared/components/toast/services/toast.service';
import { extractErrorMessage } from '@core/http/api-response.envelope';
import * as WalletActions from './wallet.actions';
import type { WalletWithdrawalPrevalidateForbiddenBody } from '../models/wallet.model';

@Injectable()
export class WalletEffects {
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  loadWallets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.loadWallets),
      switchMap(({ page, limit, append = false }) =>
        this.walletService.getWallets(page, limit).pipe(
          map(response =>
            WalletActions.loadWalletsSuccess({
              wallets: response.data,
              pagination: response.pagination,
              append,
            })
          ),
          catchError(error =>
            of(
              WalletActions.loadWalletsFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.wallets.errors.loadFailed'),
              })
            )
          )
        )
      )
    )
  );

  loadAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.loadAssets),
      switchMap(() =>
        this.walletService.getAssets().pipe(
          map(response =>
            WalletActions.loadAssetsSuccess({
              assets: response.data,
            })
          ),
          catchError(error =>
            of(
              WalletActions.loadAssetsFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.wallets.errors.loadAssetsFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  createWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.createWallet),
      switchMap(({ request }) =>
        this.walletService.createWallet(request).pipe(
          map(response =>
            WalletActions.createWalletSuccess({
              message: response.message,
              requestId: response.requestId,
            })
          ),
          catchError(error =>
            of(
              WalletActions.createWalletFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.wallets.errors.createFailed'),
              })
            )
          )
        )
      )
    )
  );

  // Success toasts for the mutations below are now driven by
  // `apiResponseInterceptor` (it surfaces the server-localized envelope
  // message automatically). Reducer-only `*Success` actions are kept; the
  // per-feature toast effects that used to plumb the message through are
  // intentionally removed.

  updateWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.updateWallet),
      switchMap(({ walletId, request }) =>
        this.walletService.updateWallet(walletId, request).pipe(
          map(response =>
            WalletActions.updateWalletSuccess({
              message: response.message,
              requestId: response.requestId,
            })
          ),
          catchError(error =>
            of(
              WalletActions.updateWalletFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.wallets.errors.updateFailed'),
              })
            )
          )
        )
      )
    )
  );

  disableWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.disableWallet),
      switchMap(({ walletId }) =>
        this.walletService.disableWallet(walletId).pipe(
          map(response =>
            WalletActions.disableWalletSuccess({
              message: response.message,
              requestId: response.requestId,
            })
          ),
          catchError(error =>
            of(
              WalletActions.disableWalletFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.wallets.errors.disableFailed'),
              })
            )
          )
        )
      )
    )
  );

  enableWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.enableWallet),
      switchMap(({ walletId }) =>
        this.walletService.enableWallet(walletId).pipe(
          map(response =>
            WalletActions.enableWalletSuccess({
              message: response.message,
              requestId: response.requestId,
            })
          ),
          catchError(error =>
            of(
              WalletActions.enableWalletFailure({
                error:
                  error?.message ||
                  this.translate.instant('assets.wallets.errors.enableFailed'),
              })
            )
          )
        )
      )
    )
  );

  retryWalletKeygen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.retryWalletKeygen),
      switchMap(({ walletId }) =>
        this.walletService.retryWalletKeygen(walletId).pipe(
          map(response =>
            WalletActions.retryWalletKeygenSuccess({
              message: response.message,
            })
          ),
          catchError(error =>
            of(
              WalletActions.retryWalletKeygenFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.wallets.errors.retryKeygenFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  loadWalletTransactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.loadWalletTransactions),
      switchMap(({ walletId, query }) =>
        this.walletService.getWalletTransactions(walletId, query).pipe(
          map(response =>
            WalletActions.loadWalletTransactionsSuccess({ response })
          ),
          catchError(error =>
            of(
              WalletActions.loadWalletTransactionsFailure({
                error:
                  error?.message ||
                  this.translate.instant(
                    'assets.wallets.errors.loadTransactionsFailed'
                  ),
              })
            )
          )
        )
      )
    )
  );

  linkWhitelistAddressToWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.linkWhitelistAddressToWallet),
      switchMap(({ walletId, whitelistAddressId }) =>
        this.walletService
          .linkWhitelistAddressToWallet(walletId, whitelistAddressId)
          .pipe(
            map(() => WalletActions.linkWhitelistAddressToWalletSuccess()),
            catchError(error =>
              of(
                WalletActions.linkWhitelistAddressToWalletFailure({
                  error: extractErrorMessage(
                    error,
                    this.translate.instant(
                      'assets.wallets.errors.linkWhitelistFailed'
                    )
                  ),
                })
              )
            )
          )
      )
    )
  );

  // `linkWhitelistAddressToWalletSuccess` no longer needs its own toast
  // effect — the server returns a localized message and
  // `apiResponseInterceptor` surfaces it.

  linkWhitelistAddressToWalletFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WalletActions.linkWhitelistAddressToWalletFailure),
        tap(({ error }) => {
          this.toastService.error(error, 3000);
        })
      ),
    { dispatch: false }
  );
  disableWalletAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.disableWalletWhitelistAddress),
      switchMap(({ walletId, whitelistAddressId }) =>
        this.walletService
          .disableWalletWhitelistAddress(walletId, whitelistAddressId)
          .pipe(
            map(() => WalletActions.disableWalletWhitelistAddressSuccess()),
            catchError(error =>
              of(
                WalletActions.disableWalletWhitelistAddressFailure({
                  error:
                    error?.error?.message ||
                    error.message ||
                    this.translate.instant(
                      'assets.wallets.errors.disableWhitelistFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  enableWalletAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.enableWalletWhitelistAddress),
      switchMap(({ walletId, whitelistAddressId }) =>
        this.walletService
          .enableWalletWhitelistAddress(walletId, whitelistAddressId)
          .pipe(
            map(() => WalletActions.enableWalletWhitelistAddressSuccess()),
            catchError(error =>
              of(
                WalletActions.enableWalletWhitelistAddressFailure({
                  error:
                    error?.error?.message ||
                    error.message ||
                    this.translate.instant(
                      'assets.wallets.errors.enableWhitelistFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  unassignWhitelistAddressFromWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.unassignWhitelistAddressFromWallet),
      switchMap(({ walletId, whitelistAddressId }) =>
        this.walletService
          .unassignWhitelistAddressFromWallet(walletId, whitelistAddressId)
          .pipe(
            map(() =>
              WalletActions.unassignWhitelistAddressFromWalletSuccess()
            ),
            catchError(error =>
              of(
                WalletActions.unassignWhitelistAddressFromWalletFailure({
                  error:
                    error?.error?.message ||
                    error?.message ||
                    this.translate.instant(
                      'assets.wallets.errors.unassignWhitelistFailed'
                    ),
                })
              )
            )
          )
      )
    )
  );

  prevalidateWithdrawal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WalletActions.prevalidateWithdrawal),
      switchMap(({ walletId }) =>
        this.walletService.prevalidateWithdrawal(walletId).pipe(
          map(response =>
            WalletActions.prevalidateWithdrawalSuccess({ walletId, response })
          ),
          catchError(error => {
            // A refusal carries the checks in `details`; the withdraw button
            // opens the modal that names the rule, so no toast here.
            if (error?.status === 403) {
              const body = (error?.error ??
                null) as WalletWithdrawalPrevalidateForbiddenBody | null;
              const verdict = body?.details;
              if (verdict?.checks?.length) {
                return of(
                  WalletActions.prevalidateWithdrawalSuccess({
                    walletId,
                    response: {
                      statusCode: 403,
                      message: body?.message ?? '',
                      timestamp: body?.timestamp ?? new Date().toISOString(),
                      data: {
                        eligible: false,
                        checks: verdict.checks,
                        failedChecks: verdict.failedChecks ?? [],
                      },
                    },
                  })
                );
              }
            }

            // The request suppresses the interceptor's toast, so any other
            // failure is toasted here, as it was before.
            const message = extractErrorMessage(
              error,
              this.translate.instant(
                'assets.wallets.errors.prevalidateWithdrawalFailed'
              )
            );
            this.toastService.error(message);
            return of(
              WalletActions.prevalidateWithdrawalFailure({
                walletId,
                error: message,
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private walletService: WalletService,
    private router: Router
  ) {}
}
